import { useState, useEffect, useRef } from 'react';

/**
 * Counts down from a target ISO timestamp.
 * Updates every second. Returns seconds remaining (floored).
 *
 * Goes NEGATIVE past expiry — a case past its timer can still be open on
 * GlobeMed (it leaves the pending list only when the agent reports it
 * gone), so the UI keeps counting "overdue" time instead of freezing.
 */
export function useCountdown(expiresAt: string | null | undefined) {
  const [secondsLeft, setSecondsLeft] = useState(() => calcRemaining(expiresAt));
  const intervalRef = useRef<ReturnType<typeof setInterval>>(undefined);

  useEffect(() => {
    setSecondsLeft(calcRemaining(expiresAt));
    if (!expiresAt) return;

    intervalRef.current = setInterval(() => {
      setSecondsLeft(calcRemaining(expiresAt));
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [expiresAt]);

  return secondsLeft;
}

function calcRemaining(expiresAt: string | null | undefined): number {
  if (!expiresAt) return 0;
  return Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
}
